import React from 'react';
import { motion } from 'framer-motion';
import { Zap, DollarSign, Leaf, Shield, Users, Cpu } from 'lucide-react';
import TweetCarousel from './TweetCarousel';
import ProcessSteps from './ProcessSteps';
import { useLanguage } from './LanguageContext';
import { useModal } from './ModalContext';
import WhataKitModal from './WhataKitModal';
import EarlyPackModal from './EarlyPackModal';
import WattModal from './WattModal';
import { EXTERNAL_LINKS } from '../config';
import './WhyHedera.css';

const STATS = [
  { icon: Zap,        value: '10,000+', unit: 'TPS',        labelEn: 'Lightning Speed',   labelEs: 'Velocidad Relámpago',   tagEn: 'Fastest',     tagEs: 'Más rápido'   },
  { icon: DollarSign, value: '$0.0008', unit: 'per tx',     labelEn: 'Ultra Low Cost',     labelEs: 'Coste Ultra Bajo',      tagEn: 'Cheapest',    tagEs: 'Más económico' },
  { icon: Leaf,       value: '100%',    unit: 'Sustainable', labelEn: 'Carbon Negative',   labelEs: 'Carbono Negativo',      tagEn: 'Greenest',    tagEs: 'Más verde'    },
  { icon: Shield,     value: 'aBFT',    unit: 'Consensus',  labelEn: 'Bank-Grade Security', labelEs: 'Seguridad Bancaria',   tagEn: 'Most Secure', tagEs: 'Más seguro'   },
  { icon: Users,      value: '50,000+', unit: 'Builders',   labelEn: 'HBarbarians',        labelEs: 'Comunidad HBarbarians', tagEn: 'Strongest',   tagEs: 'Más fuerte'   },
  { icon: Cpu,        value: 'Solidity',unit: 'Compatible', labelEn: 'Developer Friendly', labelEs: 'Amigable para Devs',    tagEn: 'Easiest',     tagEs: 'Más fácil'    },
];

const WhyHedera = () => {
  const { t, language } = useLanguage();
  const isEN = language === 'en';
  const { openModal, closeModal, isModalOpen } = useModal();
  const openWhataKit = () => openModal('whatakit');
  const openEarlyPack = () => openModal('earlypack');
  const openWatt     = () => openModal('watt');

  return (
    <section className="wh-section section" id="our-foundation">
      <div className="container">

        {/* ── Header ── */}
        <motion.div
          className="wh-header"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.35 }}
        >
          <div className="wh-header-left">
            <div className="wh-eyebrow">
              <span className="wh-eyebrow-dot" />
              {isEN ? 'our foundation' : 'nuestra base'}
            </div>
            <h2 className="wh-title">
              {isEN
                ? <><span className="wh-title-accent">Built on Hedera,</span><br />expanding everywhere.</>
                : <><span className="wh-title-accent">Construido en Hedera,</span><br />expandiendo a todas partes.</>
              }
            </h2>
          </div>
          <p className="wh-subtitle">
            {isEN
              ? 'We chose Hedera as our origin network for its speed, cost and enterprise-grade infrastructure. Now we bring these tools to every chain.'
              : 'Elegimos Hedera como nuestra red de origen por su velocidad, coste e infraestructura enterprise. Ahora llevamos estas herramientas a todas las redes.'
            }
          </p>
        </motion.div>

        {/* ── Stats grid — compact 3×2 ── */}
        <div className="wh-grid">
          {STATS.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.value}
                className="wh-stat"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
              >
                <div className="wh-stat-top">
                  <span className="wh-stat-tag">
                    {isEN ? s.tagEn : s.tagEs}
                  </span>
                  <div className="wh-stat-icon"><Icon size={18} /></div>
                </div>
                <div className="wh-stat-value">{s.value}</div>
                <div className="wh-stat-unit">{s.unit}</div>
                <div className="wh-stat-label">{isEN ? s.labelEn : s.labelEs}</div>
              </motion.div>
            );
          })}
        </div>

        {/* ── Process Steps — onboarding flow ── */}
        <ProcessSteps />

        {/* ── Vision words ── */}
        <motion.div
          className="wh-vision"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.35, delay: 0.3 }}
        >
          <h3 className="neon-text-container">
            {t('whyHedera.visionWords').split(' ').map((word, i) => (
              <span key={i} className="neon-word" style={{ animationDelay: `${i * 0.5}s` }}>
                {word}
              </span>
            ))}
          </h3>
        </motion.div>

        {/* ── Tweet Carousel ── */}
        <TweetCarousel />

      </div>

      {/* ── CTA — Ready to Join the Lab ── */}
      <div className="wh-cta-wrap">
        {/* Video background */}
        <video
          className="wh-cta-video"
          autoPlay
          loop
          muted
          playsInline
          disablePictureInPicture
          disableRemotePlayback
        >
          <source src="/videos/lab-banner-bg.mp4" type="video/mp4" />
        </video>
        <div className="wh-cta-overlay" />
        
        <div className="container">
          <motion.div
            className="ecosystem-cta text-center"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.35 }}
          >
            {/* Header — mismo patrón que ProcessSteps, centrado */}
            <div className="ps-header ps-header--center">
              <div className="ps-eyebrow">
                <span className="ps-eyebrow-dot" />
                {isEN ? 'join the lab' : 'únete al lab'}
              </div>
              <div className="ps-header-row">
                <h2 className="ps-title">
                  {isEN
                    ? <><span className="ps-title-accent">Ready to Join</span><br />the Lab?</>
                    : <><span className="ps-title-accent">¿Listo para</span><br />unirte al Lab?</>
                  }
                </h2>
              </div>
              <p className="ps-subtitle">
                {t('ecosystem.readyToJoinDesc')}
              </p>
            </div>

            <div className="cta-actions">
              <button className="btn" onClick={openEarlyPack}>{t('ecosystem.getEarlyPack')}</button>
              <button className="btn" onClick={openWatt}>{t('ecosystem.getWatt')}</button>
              <button className="btn btn--primary" onClick={openWhataKit}>{t('ecosystem.getWhataKit')}</button>
              <a href={EXTERNAL_LINKS.TOOLS_BUY} target="_blank" rel="noopener noreferrer" className="btn">
                {t('ecosystem.convertToWboost')}
              </a>
            </div>
          </motion.div>
        </div>
      </div>

      <WhataKitModal  isOpen={isModalOpen('whatakit')}  onClose={closeModal} />
      <EarlyPackModal isOpen={isModalOpen('earlypack')} onClose={closeModal} />
      <WattModal      isOpen={isModalOpen('watt')}      onClose={closeModal} />

    </section>
  );
};

export default WhyHedera;
